<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading</div>
    <div v-if="loaded" class="root">

      <!-- Top bar with hash marks -->
      <div class="forge-topbar">
        <div class="hash-marks" aria-hidden="true">
          <span v-for="n in 24" :key="n" class="hash"></span>
        </div>
        <div class="topbar-inner">
          <img v-if="loinfo && loinfo.main_logo" :src="loinfo.main_logo" class="tb-logo" />
          <div class="tb-pair">
            <span class="tb-lo">{{ loName }}</span>
            <span class="tb-sep">//</span>
            <span class="tb-re">{{ realtorName }}</span>
          </div>
        
        </div>
      </div>

      <!-- Main content -->
      <div class="forge-body">

        <!-- Hero — large and bold -->
        <section class="hero-section">
          <div class="hero-label">
            <span class="hl-bracket">[</span>
            <span class="hl-text">MORTGAGE SOLUTION</span>
            <span class="hl-bracket">]</span>
          </div>
          <h1 class="forge-h1" v-html="heroH1Html"></h1>
          <div class="hero-sub-row">
            <div class="angle-bar"></div>
            <p class="hero-p">{{ heroBody }}</p>
          </div>
          <div class="forge-btns">
            <a class="forge-btn-primary" :href="applyRightLink">{{ heroPrimaryText }}</a>
            <a v-if="applyLeftLink" class="forge-btn-sec" :href="applyLeftLink">{{ heroSecondaryText }}</a>
          </div>
          <div class="forge-contacts">
            <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="fc-item">
              <span class="fc-arrow">›</span>{{ loinfo.phone_number }}
            </a>
            <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="fc-item">
              <span class="fc-arrow">›</span>{{ loinfo.email }}
            </a>
          </div>
        </section>

        <!-- Sidebar cards -->
        <aside class="forge-sidebar">

          <div class="forge-card lo-fcard">
            <div class="fc-header">
              <span class="fch-code">LO_001</span>
              <span class="fch-status lo-status">● ACTIVE</span>
            </div>
            <div class="fc-body">
              <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="fc-img" />
              <div class="fc-info">
                <div class="fc-name">{{ loName }}</div>
                <div class="fc-role">{{ loTitle }}</div>
                <div v-if="loinfo && loinfo.nmls" class="fc-nmls">{{ (loinfo.nmls || "").trim() }}</div>
              </div>
            </div>
            <div class="fc-specs">
              <div class="spec-row">
                <span class="spec-key">PHONE</span>
                <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="spec-val lo-val">{{ loinfo.phone_number }}</a>
              </div>
              <div class="spec-row">
                <span class="spec-key">EMAIL</span>
                <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="spec-val lo-val truncate">{{ loinfo.email }}</a>
              </div>
            </div>
            
          </div>

          <div class="forge-card re-fcard">
            <div class="fc-header">
              <span class="fch-code">RE_002</span>
              <span class="fch-status re-status">● PARTNER</span>
            </div>
            <div class="fc-body">
              <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="fc-img" />
              <div class="fc-info">
                <div class="fc-name">{{ realtorName }}</div>
                <div class="fc-role">{{ realtor && realtor.brokerage_name }}</div>
                <div v-if="realtor && realtor.nmls" class="fc-nmls">NMLS {{ (realtor.nmls || "").trim() }}</div>
              </div>
            </div>
            <div class="fc-specs">
              <div class="spec-row" v-if="realtor && realtor.phone_number">
                <span class="spec-key">PHONE</span>
                <a :href="'tel:' + realtor.phone_number" class="spec-val re-val">{{ realtor.phone_number }}</a>
              </div>
              <div class="spec-row" v-if="realtor && realtor.email">
                <span class="spec-key">EMAIL</span>
                <a :href="'mailto:' + realtor.email" class="spec-val re-val truncate">{{ realtor.email }}</a>
              </div>
              <div class="spec-row" v-if="realtor && realtor.website_url">
                <span class="spec-key">WEB</span>
                <a :href="realtor.website_url" class="spec-val re-val" target="_blank" rel="noreferrer">Visit →</a>
              </div>
            </div>
          </div>

        </aside>
      </div>

      <!-- Bottom bar -->
      <div class="forge-footer">
        <span class="ff-item">{{ loName }}</span>
        <span class="ff-sep">—</span>
        <span class="ff-item">{{ loTitle }}</span>
        <div class="ff-flex"></div>
        <span class="ff-right">NMLS {{ loinfo && loinfo.nmls }}</span>
      </div>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending: { primary: "#eb610a", accent: "#646363" },
  radiantmortgage: { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage: { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam: { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage: { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage: { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial: { primary: "#0b1726", accent: "#c69c39" },
};
function getBrandKey(email) {
  if (!email) return null;
  return email.slice(email.indexOf("@") + 1).toLowerCase().split(".")[0] || null;
}
export default {
  name: "CoBrandForge",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Built for the<br/><em>road ahead.</em>";
      const parts = h.split(",");
      if (parts.length > 1) return `${parts[0].trim()}<br/><em>${parts.slice(1).join(",").trim()}</em>`;
      return h;
    },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      return [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim()
        || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500&family=Bebas+Neue&family=IBM+Plex+Sans:wght@300;400;500;600&display=swap');

.page {
  min-height: 100vh;
  background: #111318;
  color: #c8ccd8;
  font-family: 'IBM Plex Sans', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --bg: #111318;
  --card-bg: #18191f;
  --border: rgba(200,204,216,0.1);
  --muted: #7a8098;
  overflow: hidden;
  position: relative;
}
.root { display: flex; flex-direction: column; min-height: 100vh; }

/* TOPBAR */
.forge-topbar { background: #0a0b0e; border-bottom: 1px solid var(--border); }
.hash-marks { display: flex; height: 4px; overflow: hidden; }
.hash { flex: 1; background: var(--a); opacity: 0.3; }
.hash:nth-child(odd) { opacity: 0; }
.topbar-inner { display: flex; align-items: center; justify-content: space-between; padding: 14px 48px; }
.tb-logo { width: 140px; max-height:72px; object-fit: contain; filter: brightness(0) invert(1); opacity: 0.7; }
.tb-pair { display: flex; align-items: center; gap: 12px; font-family: 'IBM Plex Mono', monospace; font-size: 11px; }
.tb-lo { color: #c8ccd8; }
.tb-sep { color: var(--a); opacity: 0.6; }
.tb-re { color: var(--muted); }
.tb-cta { font-family: 'IBM Plex Mono', monospace; font-size: 11px; font-weight: 500; text-decoration: none; background: var(--a); color: #111318; padding: 8px 18px; border-radius: 3px; letter-spacing: 0.04em; transition: opacity 0.2s; }
.tb-cta:hover { opacity: 0.85; }

/* BODY */
.forge-body { display: grid; grid-template-columns: 1fr 340px; flex: 1; max-width: 1200px; margin: 0 auto; width: 100%; padding: 56px 48px; gap: 48px; align-items: start; }

/* HERO */
.hero-section { }
.hero-label { display: flex; align-items: center; gap: 6px; margin-bottom: 18px; font-family: 'IBM Plex Mono', monospace; font-size: 10px; letter-spacing: 0.18em; color: var(--muted); }
.hl-bracket { color: var(--a); }
.forge-h1 { font-family: 'Bebas Neue', sans-serif; font-size: clamp(52px, 7vw, 96px); line-height: 0.95; letter-spacing: 0.02em; color: #e8ecf5; margin: 0 0 28px; }
.forge-h1 em { color: var(--a); font-style: normal; }
.hero-sub-row { display: flex; gap: 20px; align-items: flex-start; margin-bottom: 32px; }
.angle-bar { width: 3px; flex-shrink: 0; background: var(--a); border-radius: 2px; margin-top: 3px; align-self: stretch; }
.hero-p { font-size: 14px; line-height: 1.76; color: var(--muted); font-weight: 300; }
.forge-btns { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 24px; justify-content: center;}
.forge-btn-primary { font-family: 'IBM Plex Mono', monospace; font-size: 11px; font-weight: 500; text-decoration: none; background: var(--a); color: #111318; padding: 12px 24px; border-radius: 3px; letter-spacing: 0.06em; transition: opacity 0.2s; width: 55%; text-align: center; }
.forge-btn-primary:hover { opacity: 0.88; }
.forge-btn-sec { font-family: 'IBM Plex Mono', monospace; font-size: 11px; text-decoration: none; color: var(--muted); border: 1px solid var(--border); padding: 12px 20px; border-radius: 3px; transition: border-color 0.18s; }
.forge-btn-sec:hover { border-color: var(--a); color: var(--a); }
.forge-contacts { display: flex; flex-direction: column; gap: 6px; }
.fc-item { font-family: 'IBM Plex Mono', monospace; font-size: 11px; text-decoration: none; color: var(--muted); display: flex; align-items: center; gap: 8px; transition: color 0.18s; }
.fc-item:hover { color: var(--a); }
.fc-arrow { color: var(--a); }

/* SIDEBAR */
.forge-sidebar { display: flex; flex-direction: column; gap: 14px; }
.forge-card { background: var(--card-bg); border: 1px solid var(--border); border-radius: 4px; overflow: hidden; }
.fc-header { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: rgba(255,255,255,0.025); border-bottom: 1px solid var(--border); }
.fch-code { font-family: 'IBM Plex Mono', monospace; font-size: 9px; color: var(--muted); letter-spacing: 0.12em; }
.fch-status { font-family: 'IBM Plex Mono', monospace; font-size: 9px; letter-spacing: 0.1em; }
.lo-status { color: color-mix(in srgb, var(--a) 80%, #aac); }
.re-status { color: #60d8a0; }
.fc-body { display: flex; gap: 12px; align-items: flex-start; padding: 14px; }
.fc-img { width: 48px; border-radius: 4px; object-fit: cover; flex-shrink: 0; border: 1px solid var(--border); }
.fc-name { font-size: 15px; font-weight: 600; color: #e8ecf5; }
.fc-role { font-size: 11px; color: var(--muted); margin-top: 2px; }
.fc-nmls { font-family: 'IBM Plex Mono', monospace; font-size: 10px; color: var(--muted); opacity: 0.6; margin-top: 2px; }
.fc-specs { padding: 0 14px 12px; display: flex; flex-direction: column; gap: 6px; border-top: 1px solid var(--border); padding-top: 10px; }
.spec-row { display: flex; gap: 10px; align-items: center; }
.spec-key { font-family: 'IBM Plex Mono', monospace; font-size: 8px; color: var(--muted); letter-spacing: 0.12em; width: 36px; flex-shrink: 0; opacity: 0.7; }
.spec-val { font-size: 10px; text-decoration: none; color: var(--muted); transition: color 0.18s; }
.truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 200px; display: inline-block; }
.lo-val:hover { color: var(--a); }
.re-val:hover { color: #60d8a0; }
.fc-cta { display: block; margin: 0 14px 14px; text-align: center; text-decoration: none; font-family: 'IBM Plex Mono', monospace; font-size: 10px; font-weight: 500; letter-spacing: 0.08em; padding: 10px; border-radius: 3px; background: var(--a); color: #111318; transition: opacity 0.2s; }
.fc-cta:hover { opacity: 0.88; }

/* FOOTER */
.forge-footer { border-top: 1px solid var(--border); padding: 14px 48px; display: flex; align-items: center; gap: 0; background: #0a0b0e; }
.ff-item { font-family: 'IBM Plex Mono', monospace; font-size: 10px; color: var(--muted); }
.ff-sep { margin: 0 12px; color: var(--border); }
.ff-flex { flex: 1; }
.ff-right { font-family: 'IBM Plex Mono', monospace; font-size: 10px; color: var(--muted); opacity: 0.5; }

@media (max-width: 820px) {
  .forge-body { grid-template-columns: 1fr; padding: 32px 24px; }
  .topbar-inner { padding: 12px 24px; }
  .tb-pair { display: none; }
  .forge-footer { padding: 12px 24px; }
}
</style>
